import { type InputRef, Table } from 'antd';
import type { ColumnFilterItem, FilterDropdownProps } from 'antd/lib/table/interface';
import { isEmpty, pick, sortBy } from 'lodash/fp';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type LanguagePackListDataFragment } from '../../../api/fragments/LanguagePackListData';
import { useGetLanguagePackListFiltersQuery } from '../../../api/queries/getLanguagePackListFilters';
import { useListLanguagePacksQuery } from '../../../api/queries/listLanguagePacks';
import { type LanguageOrientation, LanguagePackSortingField } from '../../../api/types';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import FilterBox from '../../../components/FilterBox';
import PaginatedTable from '../../../components/PaginatedTable';
import InternalErrorResult from '../../../components/results/InternalErrorResult';
import SearchBox from '../../../components/SearchBox';
import makeGetSortingRule from '../../../utilities/makeGetSortingRule';
import renderFilterIcon from '../../../utilities/renderFilterIcon';
import renderSearchIcon from '../../../utilities/renderSearchIcon';
import useLanguageList from '../../../utilities/useLanguageList';
import useLanguagePackSortOrder from './useLanguagePackSortOrder';
import useListReducer from './useListReducer';

const LanguagePackList = () => {
    const { t } = useTranslation(['languagePackList']);

    const navigate = useNavigate();
    const company = useCompany(true);

    const { data: langData, loading: langLoading } = useLanguageList();

    // get state from a reducer
    const [state, dispatch] = useListReducer();
    const inputRef = useRef<InputRef>(null);

    // fetch data
    const { page, pageSize, sort, filter } = state;
    const { data, loading, error } = useListLanguagePacksQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            pagination: { offset: (page - 1) * pageSize, limit: pageSize },
            sort: pick(['field', 'order'], sort),
            filter: {
                ...filter,
            },
        },
    });

    const { data: languagePackOrientationList } = useGetLanguagePackListFiltersQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter: {
                companyId: company?.id,
            },
        },
    });

    const orientationList = useMemo(() => {
        const filterList = languagePackOrientationList?.languagePackListFilters?.orientation;
        if (!filterList) {
            return [];
        }

        return sortBy(
            'text',
            filterList.map(item => ({
                text: t(`languagePackDetails:options.orientation.${item}`),
                value: item,
            }))
        );
    }, [languagePackOrientationList?.languagePackListFilters?.orientation, t]);

    const { referenceNameSort, displayNameSort, internationalCodeSort } = useLanguagePackSortOrder(sort);

    // prepare items as a data source
    const dataSource = useMemo(
        () =>
            (data?.languages?.items || []).map(item => ({
                ...item,
                key: item.id,
                code: langData?.find(lang => lang?.value === item?.code)?.label || item?.code,
            })),
        [data, langData]
    );
    const total = data?.languages?.count || 0;

    const showSearchDropDown = useCallback(
        (props: FilterDropdownProps, dataIndex: string) =>
            SearchBox({
                filterDropDownProps: props,
                dataIndex: t(`languagePackList:columns.${dataIndex}`),
                onRef: node => {
                    inputRef.current = node;
                },
            }),
        [inputRef]
    );

    const showFilterDropDown = useCallback(
        (props: FilterDropdownProps, filters: ColumnFilterItem[]) =>
            FilterBox({
                filterDropDownProps: props,
                filters,
            }),
        []
    );

    const getSortField = makeGetSortingRule((field): LanguagePackSortingField => {
        switch (field) {
            case 'referenceName':
                return LanguagePackSortingField.ReferenceName;

            case 'displayName':
                return LanguagePackSortingField.DisplayName;

            case 'code':
                return LanguagePackSortingField.InternationalCode;

            default:
                throw new Error('Such Field is not supported');
        }
    });

    const onChange = useCallback(
        (pagination, filters, sorter, extra) => {
            switch (extra.action) {
                case 'sort': {
                    return dispatch({
                        type: 'setSort',
                        sortBy: getSortField(sorter),
                    });
                }

                case 'filter': {
                    return dispatch({
                        type: 'setFilter',
                        filteringRule: {
                            referenceName: !filters.referenceName ? undefined : (filters.referenceName[0] as string),
                            displayName: !filters.displayName ? undefined : (filters.displayName[0] as string),
                            orientation: !filters.orientation
                                ? undefined
                                : (filters.orientation as LanguageOrientation[]),
                        },
                    });
                }

                default:
                    return undefined;
            }
        },
        [dispatch, getSortField]
    );

    if (!loading && !langLoading) {
        if (error) {
            return <InternalErrorResult />;
        }
    }

    return (
        <PaginatedTable
            dataSource={dataSource}
            dispatch={dispatch}
            loading={loading}
            onChange={onChange}
            onRow={(row: LanguagePackListDataFragment) => ({
                onClick: () => {
                    navigate(`/admin/system/languages/${row.id}`);
                },
                style: { cursor: 'pointer' },
            })}
            rowKey="id"
            state={state}
            tableName={t('languagePackList:title')}
            total={total}
        >
            <Table.Column
                key="referenceName"
                dataIndex="referenceName"
                filterDropdown={props => showSearchDropDown(props, 'referenceName')}
                filterIcon={renderSearchIcon}
                filteredValue={filter.referenceName ? [filter.referenceName] : undefined}
                sortOrder={referenceNameSort}
                title={t('languagePackList:columns.referenceName')}
                sorter
            />
            <Table.Column
                key="displayName"
                dataIndex="displayName"
                filterDropdown={props => showSearchDropDown(props, 'displayName')}
                filterIcon={renderSearchIcon}
                filteredValue={filter.displayName ? [filter.displayName] : undefined}
                sortOrder={displayNameSort}
                title={t('languagePackList:columns.displayName')}
                sorter
            />
            <Table.Column
                key="code"
                dataIndex="code"
                sortOrder={internationalCodeSort}
                title={t('languagePackList:columns.code')}
                sorter
            />
            <Table.Column
                key="orientation"
                dataIndex="orientation"
                filterDropdown={props => showFilterDropDown(props, orientationList)}
                filterIcon={renderFilterIcon}
                filteredValue={filter.orientation}
                onFilter={(value: string, record: LanguagePackListDataFragment) =>
                    record.orientation.indexOf(value) === 0
                }
                render={value => t(`languagePackDetails:options.orientation.${value}`)}
                title={t('languagePackList:columns.orientation')}
            />
        </PaginatedTable>
    );
};

export default LanguagePackList;
